import { AccessToken } from '@shared/api/common/access-token';
import { createNilReducer, createReducer, select } from '../core';
import { StepStatus } from '../models/step-status';
import { RootState } from '../store';
import { ChallengeCriteria, MfaMethod } from '@shared/api/identity/models/challenge-accepted.response';
import { CheckRequirementType } from '@shared/api/identity/models/check-requirement-type.enum';

export type LoginState = {
  current?: CurrentLogin;
  unsupportedBrowser?: UnsupportedBrowserCache;
};

type CurrentLogin = {
  expiresAt: string;
  info: {
    challengeId: string;
    userId: string;
    locale: string;
  }
  exit?: LoginExit;
  tokens: {
    check?: AccessToken;
    identity?: AccessToken;
    mfa?: AccessToken;
  };
  steps: LoginStep[];
}

type UnsupportedBrowserCache = {
  verificationImageId?: string;
  loginUrl?: string;
  /** Exit preserved from the originating session, used to complete the login once the user has returned from completing checks. */
  exit?: LoginExit;
}

export type LoginStepType = LoginStep['type'];
export type ExactLoginStep<T extends LoginStepType> = LoginStep & { type: T };
type LoginStepArgs<T extends LoginStepType> = ExactLoginStep<T>['args'];

export type LoginStep = {
  status: StepStatus;
  optional: boolean;
  args?: {};
  data?: {};
} & (
  | {
      type: 'mfa';
      args: {
        method: MfaMethod;
        mfaVerificationId?: string;
        email?: string;
        phoneNumber?: string;
      }
      data?: {
        mfaProof: string;
      };
    }
  | {
      type: 'face_check';
      args: {
        method: 'standard' | 'iproov';
        attemptCount: number;
      };
      data?: {};
    }
  | {
      type: 'voice_check';
      args: {
        locale: string;
      };
      data?: {};
    }
  | {
      type: 'webauthn';
      data?: {};
    }
  | {
      type: 'polling';
    }
  | {
      type: 'code';
      data?: {};
    }
  | {
      type: 'choose_email';
      data?: {
        email: string;
      };
    }
);

export type LoginExit =
  | {
      type: 'auth';
      authCode: string;
    }
  | {
      type: 'oauth';
      authCode: string;
      openIdServer: string;
      applicationName: string;
    }
  | {
      type: 'redirect';
      redirectUrl: string;
    }
  | {
      type: 'phone';
      dialledNumber: string;
    };

type TokenName = 'mfa' | 'identity' | 'check';
type TokenUpdate = Partial<Record<TokenName, AccessToken>>;
function updateTokens(reg: CurrentLogin, update: TokenUpdate) {
  if (update.check) {
    reg.tokens.check = update.check;
  }
  if (update.identity) {
    reg.tokens.identity = update.identity;
  }
  if (update.mfa) {
    reg.tokens.mfa = update.mfa;
  }
}

export function newLoginState(): LoginState {
  return {
    current: undefined,
    unsupportedBrowser: undefined,
  }
}

export const loadLoginSlice = createReducer((state, payload: LoginState) => {
  state.login = {
    ...newLoginState(),
    ...payload,
  }
});

type NewLoginStepConfig<T extends LoginStepType> = {
  type: T;
  args: LoginStepArgs<T>;
  optional?: boolean;
};
export function newLoginStep<T extends LoginStepType = 'mfa'>(config: NewLoginStepConfig<T>) {
  return {
    type: config.type,
    args: config.args,
    optional: config.optional ?? false,
    status: StepStatus.Idle,
  };
}

type NewLoginConfig = {
  expiresAt: string;
  challengeId: string;
  userId: string;
  locale: string;
}
function newLogin(config: NewLoginConfig): CurrentLogin {
  const { expiresAt, challengeId, locale, userId } = config;

  return {
    expiresAt,
    info: {
      challengeId,
      userId,
      locale,
    },
    tokens: {},
    steps: [],
  }
}

// Reducers

type StartNewLoginPayload = {
  expiresAt: string;
  challengeId: string;
  userId: string;
  locale: string;
  email?: string;
  phoneNumber?: string;
  criteria: ChallengeCriteria[];
  additionalSteps: {
    webauthn?: boolean;
    chooseEmail?: boolean;
  }
}
export const startNewLogin = createReducer((state, payload: StartNewLoginPayload) => {
  const { expiresAt, challengeId, userId, locale, email, phoneNumber, criteria, additionalSteps } = payload;
  const login = newLogin({ expiresAt, challengeId, userId, locale });
  state.login.current = login;

  const criteriaMap = new Map<CheckRequirementType, ChallengeCriteria>();
  criteria.forEach(c => {
    criteriaMap.set(c.type, c);
  });

  const mfaCriterion = criteriaMap.get(CheckRequirementType.Mfa)
  if (mfaCriterion) {
    login.steps.push(newLoginStep({
      type: "mfa",
      args: {
        method: mfaCriterion.mfa_method ?? MfaMethod.Email,
        mfaVerificationId: mfaCriterion.mfa_id,
        email,
        phoneNumber,
      }
    }));
  }

  if (criteriaMap.has(CheckRequirementType.FaceMatch)) {
    login.steps.push(newLoginStep({ type: "face_check", args: { method: "standard", attemptCount: 0 } }));
  }
  if (criteriaMap.has(CheckRequirementType.IproovMatch) || criteriaMap.has(CheckRequirementType.IproovExpress)) {
    login.steps.push(newLoginStep({ type: "face_check", args: { method: "iproov", attemptCount: 0 } }));
  }
  if (criteriaMap.has(CheckRequirementType.VoiceMatch)) {
    login.steps.push(newLoginStep({ type: "voice_check", args: { locale } }));
  }
  login.steps.push(newLoginStep({ type: "polling", args: {} }));
  if (additionalSteps?.webauthn) {
    login.steps.push(newLoginStep({ type: "webauthn", args: {} }));
  }
  if (additionalSteps?.chooseEmail) {
    login.steps.push(newLoginStep({ type: "choose_email", args: {} }));
  }
})

export const deleteLogin = createReducer((state, payload: { preserveUnsupportedBrowser?: boolean }) => {
  state.login.current = undefined;
  if (!payload.preserveUnsupportedBrowser) {
    state.login.unsupportedBrowser = undefined;
  }
});

/**
 * Ensure a current login session exists, creating a minimal one if it doesn't.
 *
 * Used by flows that authenticate the user directly and can be entered before a
 * login session has been set up (e.g. the unsupported browser flow, which is
 * reachable straight from the initial sign-on screen). The `exit` preserved from
 * the originating session (e.g. the OAuth server details) is applied so the login
 * can be completed correctly. If a login already exists it is left untouched.
 */
export const ensureLogin = createReducer((state, payload: { userId: string; expiresAt: string; exit?: LoginExit }) => {
  if (state.login.current) {
    return;
  }
  const login = newLogin({
    expiresAt: payload.expiresAt,
    challengeId: '',
    userId: payload.userId,
    locale: '',
  });
  login.exit = payload.exit;
  state.login.current = login;
});

/** Provide a prepared valid registration exit for this registration. */
export const markLoginExit = createReducer((state, payload: { exit: LoginExit | undefined }) => {
  const { exit } = payload;
  const login = select(state, selectLogin());
  if (!login || !exit) {
    return;
  }
  login.exit = payload.exit;
});

/** Mark this registration as an OAuth registration. */
export const markLoginIsOauth = createReducer((state, payload: { applicationName: string; authCode: string; openIdServer: string }) => {
  const login = select(state, selectLogin());
  if (!login) {
    return;
  }
  login.exit = {
    type: 'oauth',
    authCode: payload.authCode,
    applicationName: payload.applicationName,
    openIdServer: payload.openIdServer,
  };
});

/** Mark this loginistration as an auth loginistration. */
export const markLoginIsAuth = createReducer((state, payload: { authCode: string }) => {
  const login = select(state, selectLogin());
  if (!login) {
    return;
  }
  login.exit = {
    type: 'auth',
    authCode: payload.authCode,
  };
});

/**
 * Mark a login step as complete, and store any data and/or tokens from that step.
 */
export const completeLoginStep = createReducer((state, payload: Pick<LoginStep, 'type' | 'data'> & { tokens?: TokenUpdate }) => {
  const { type, data, tokens } = payload;
  const step = select(state, selectLoginStep(type));
  if (!step) {
    console.warn(`Instructed to complete step "${step}", but no such step exists in this registration`);
    return;
  }
  step.status = StepStatus.Complete;
  if (data) {
    step.data = data;
  }
  if (tokens) {
    const login = select(state, selectLogin());
    if (!login) {
      return;
    }
    updateTokens(login, tokens);
  }
});

/**
 * Remove all remaining login steps.
 *
 * Used when switching to the unsupported browser flow: the checks are completed
 * on another device and confirmed by the code verification, so any steps still
 * pending in this session must be cleared. With no steps left, entering the code
 * takes the user straight to the login's exit action.
 */
export const clearLoginSteps = createNilReducer((state) => {
  const login = select(state, selectLogin());
  if (!login) {
    return;
  }
  login.steps = [];
});

/**
 * Clear a login step so it has to be re-done.
 */
export const redoLoginStep = createReducer((state, payload: { type: LoginStepType }) => {
  const { type } = payload;
  const step = select(state, selectLoginStep(type));
  if (!step) {
    console.warn(`Instructed to redo step "${step}", but no such step exists in this registration`);
    return;
  }
  step.status = StepStatus.Idle;
  step.data = undefined;
});

/**
 * Record another face check attempt (a standard upload or an iProov token request).
 */
export const incrementFaceCheckAttempt = createNilReducer((state) => {
  const step = select(state, selectLoginStep('face_check'));
  if (!step) {
    console.warn(`Instructed to increment the face check attempt count, but no face check step exists in this login`);
    return;
  }
  step.args.attemptCount++;
});

type UpdateArgsPayload<T extends LoginStepType = any> = { type: T; args: Partial<LoginStepArgs<T>> };
/**
 * Update the arguments of a given step.
 *
 * This will add to the current arguments.
 */
const _updateLoginStepArgs = createReducer((state: RootState, payload: UpdateArgsPayload) => {
  const { type, args } = payload;
  const step = select(state, selectLoginStep(type));
  if (!step) {
    console.warn(`Instructed to set args for step "${step}", but no such step exists in this login`);
    return;
  }
  step.args = {
    ...(step.args ?? {}),
    ...args,
  };
});
export const updateLoginStepArgs = <T extends LoginStepType>(payload: UpdateArgsPayload<T>) =>
  _updateLoginStepArgs(payload);


/** Add one or more access tokens to the current login. */
export const putLoginTokens = createReducer((state, payload: TokenUpdate) => {
  const login = select(state, selectLogin());
  if (!login) {
    console.warn("Cannot put login tokens; login doesn't exist");
    return;
  }
  updateTokens(login, payload);
});

export const setLoginVoiceLocale = createReducer((state, payload: { locale: string }) => {
  const step = select(state, selectLoginStep('voice_check'));
  if (!step) {
    return;
  }
  step.args.locale = payload.locale;
});

export const addLoginChooseEmailStep = createNilReducer((state) => {
  const login = select(state, selectLogin());
  if (!login) {
    return;
  }
  const exists = select(state, selectLoginStep("choose_email"))
  if (exists) {
    console.log("Choose email step aready existed, aborting.")
    return;
  }
  login.steps = [
    ...login.steps,
    newLoginStep({ type: "choose_email", args: {} }),
  ]
});

export const updateLoginLocale = createReducer((state, payload: { locale: string }) => {
  const { locale } = payload;
  if (!locale) {
    return;
  }
  const login = select(state, selectLogin());
  if (!login) {
    return;
  }
  login.info.locale = locale;
  const voice = select(state, selectLoginStep("voice_check"));
  if (voice) {
    voice.args.locale = locale;
  }
});

export const updateLoginUserId = createReducer((state, payload: { userId: string }) => {
  const { userId } = payload;
  if (!userId) {
    return;
  }
  const login = select(state, selectLogin());
  if (!login) {
    return;
  }
  login.info.userId = userId;
});

export const updateUnsupportedLoginInfo = createReducer((state, payload: Partial<UnsupportedBrowserCache>) => {
  state.login.unsupportedBrowser = {
    ...(state.login.unsupportedBrowser ?? {}),
    ...payload,
  };
});

// Selectors

/** Select the current login session. */
export const selectLogin = () => (state: RootState) => {
  return state.login.current;
}

/**
 * Get a specific step from the current login.
 */
export const selectLoginStep =
  <T extends LoginStepType>(type: T) =>
  (state: RootState) => {
    return state.login.current?.steps.find((step) => step.type === type) as ExactLoginStep<T>;
  };

export const selectUnsupportedLoginInfo = () => (state: RootState) => {
  return state.login.unsupportedBrowser;
}

/**
 * Get the auth code found in the login exit.
 * @returns The auth code, if one exists.
 */
export const selectLoginAuthCode = () => (state: RootState) => {
  const exit = select(state, selectLoginExit());
  if (exit && "authCode" in exit) {
    return exit.authCode;
  }
  return undefined;
}

export const selectLoginExit = () => (state: RootState) => {
  return state.login.current?.exit;
}

/** Select the next idle login step. */
export const selectNextLoginStep = () => (state: RootState) => {
  const login = select(state, selectLogin());
  if (!login) {
    return undefined;
  }
  return login.steps.find((s) => s.status === StepStatus.Idle);
}

/**
 * Get an access token stored for this login.
 */
export const selectLoginToken =
  (token: TokenName) =>
  (state: RootState): AccessToken | undefined => {
    const login = select(state, selectLogin());
    if (!login) {
      return undefined;
    }
    switch (token) {
      case 'check':
        return login.tokens.check;
      case 'identity':
        return login.tokens.identity;
      case 'mfa':
        return login.tokens.mfa;
    }
  };

export const selectLoginChallengeId = () => (state: RootState) => {
  const login = select(state, selectLogin());
  return login?.info?.challengeId;
}
